import {
  createElement,
  forwardRef,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDesignTokens, renderInlineCssVars, DesignTokens } from '../../theme';
import { BaseMenuButton } from '../MenuButton/__internal__';
import { BaseButton } from '../Button/__internal__';
import { ButtonVariant } from '../Button';
import { MenuSection } from '../Menu';
import { Icon } from '../Icon';
import { Text } from '../Text';
import { postfixTestId } from '../../util/testId';
import { ReactKeyboardEvent } from '../../react-synthetic-events';
import { setAlpha } from '../../design-tokens';
import { CloseTab } from '@sas/react-svg';
import { useIconA11yInfo } from '../Icon/useIconA11yInfo';
import { useIsRTL } from '../../locale';
import { isNullish } from '@sas/nova-core/typeguards';
import { setRef } from '../../util/ref-util';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { TokenDesignProps, TokenVariant } from './types';
import { getLogger } from '@sas/nova-core/logging';
import { TokenDesignContainer } from './__internal__';
import bundle from './NovaComponents-Token-gui-icu.properties';
import cx from 'classnames';
import cn from './Token.css.json';
import './Token.css';

const useTextLocalizer = createUseTextLocalizer(bundle);
const useTokenDndPrefixId = createUseHtmlId('Token-dndPrefix');
const logger = getLogger('TokenDesign');

type FocusableElementType = HTMLDivElement | null;

export const TokenDesign = forwardRef<HTMLDivElement, TokenDesignProps>(function TokenDesign(
  props,
  ref
) {
  if (props.value.trim() === '') {
    logger.error('A text label must be present on the token and the text label cannot be blank.');
  }
  const {
    variant,
    testId,
    onTokenFocusBlur,
    keyInteraction,
    hasTokenCountChanged,
    isFirstToken,
    isLastToken,
  } = props;

  const localize = useTextLocalizer();
  const defaultDndPrefix = useTokenDndPrefixId();
  const dndPrefix = props.dndPrefix ?? defaultDndPrefix;
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const textRef = useRef<FocusableElementType>(null);
  const closeRef = useRef<HTMLDivElement | null>(null);
  // ref for maintaining focus on either text/menubutton or close icon
  // 0 corresponds to text/menubutton element and 1 corresponds to close icon container element
  const focusRef = useRef(0);
  const isInitialRender = useRef(true);
  const isRTL = useIsRTL();
  const tokenContainerRef = useRef<HTMLElement>();

  const tabIndex = props.tabIndex ?? 0;

  let menuSections: MenuSection[] = [];
  if (props.includeDropdown) {
    if (props.menuSections && props.menuSections.length > 0) {
      menuSections = props.menuSections;
    } else if (props.menuItems && props.menuItems.length > 0) {
      menuSections = [{ items: props.menuItems }];
    }
  }

  switch (keyInteraction) {
    case 'right':
    case 'ctrlHome':
      focusRef.current = 0;
      break;
    case 'left':
    case 'ctrlEnd':
      if (!isNullish(closeRef.current)) {
        focusRef.current = 1;
      } else {
        focusRef.current = 0;
      }
      break;
  }

  const setTokenContainerRef = useCallback(
    (tokenContainer: HTMLElement | null) => {
      if (tokenContainer) {
        tokenContainerRef.current = tokenContainer;
        setRef(ref, tokenContainer);
      }
    },
    [ref]
  );

  const dt = useDesignTokens();
  const getIconA11yInfo = useIconA11yInfo();
  const iconA11yInfo = props.icon ? getIconA11yInfo(props.icon) : undefined;
  const themeTokens = getTokenThemeTokens(dt, variant, props.readOnly);

  const style = renderInlineCssVars(
    {
      ...themeTokens,
      'spacing-squish-xs': dt.spacing.insetSquish.xs.padding,
      'spacing-s': dt.spacing.core.s,
      'spacing-xs': dt.spacing.core.xs,
      'spacing-xxs': dt.spacing.core.xxs,
      'font-size-s': dt.enableNovaTokens ? dt.fontNova.size.s : dt.font.size.s,
    },
    'token'
  );

  const unsetTabIndex = useCallback((...elements: FocusableElementType[]) => {
    for (const element of elements) {
      element?.setAttribute('tabindex', '-1');
    }
  }, []);

  const setTabIndex = useCallback((element: FocusableElementType) => {
    element?.setAttribute('tabindex', '0');
  }, []);

  const onFocusChange = useCallback(
    (
      elementToFocus: FocusableElementType,
      elementToBlur: FocusableElementType,
      columnToFocus: number
    ) => {
      focusRef.current = columnToFocus;
      unsetTabIndex(elementToBlur);
      setTabIndex(elementToFocus);
      elementToFocus?.focus();
    },
    [setTabIndex, unsetTabIndex]
  );

  function defaultOnKeyDown(e: ReactKeyboardEvent) {
    switch (e.key) {
      case 'ArrowDown':
      case isRTL ? 'ArrowLeft' : 'ArrowRight':
        e.preventDefault();
        if (focusRef.current === 0) {
          if (!isNullish(closeRef.current)) {
            onFocusChange(closeRef.current, textRef.current, 1);
          } else {
            onTokenFocusBlur?.('right');
          }
        } else if (focusRef.current === 1) {
          if (onTokenFocusBlur && !isLastToken) {
            unsetTabIndex(textRef.current, closeRef.current);
            onTokenFocusBlur?.('right');
          }
        }
        break;

      case 'ArrowUp':
      case isRTL ? 'ArrowRight' : 'ArrowLeft':
        e.preventDefault();
        if (focusRef.current === 0) {
          if (onTokenFocusBlur && !isFirstToken) {
            unsetTabIndex(textRef.current, closeRef.current);
            onTokenFocusBlur?.('left');
          }
        } else if (focusRef.current === 1) {
          onFocusChange(textRef.current, closeRef.current, 0);
        }
        break;

      case 'Home':
        e.preventDefault();
        if (e.ctrlKey) {
          unsetTabIndex(textRef.current, closeRef.current);
          onTokenFocusBlur?.('ctrlHome');
        } else {
          onFocusChange(textRef.current, closeRef.current, 0);
        }
        break;

      case 'End':
        e.preventDefault();
        if (e.ctrlKey) {
          unsetTabIndex(textRef.current, closeRef.current);
          onTokenFocusBlur?.('ctrlEnd');
        } else {
          if (!isNullish(closeRef.current)) {
            onFocusChange(closeRef.current, textRef.current, 1);
          } else {
            onFocusChange(textRef.current, closeRef.current, 0);
          }
        }
        break;
      default:
        return;
    }
  }

  useEffect(() => {
    if (isInitialRender.current && !hasTokenCountChanged) {
      isInitialRender.current = false;
      return;
    }
    // focus on token's text/menubutton or close icon based on focusRef value.
    if (tabIndex === 0) {
      switch (focusRef.current) {
        case 0:
          onFocusChange(textRef.current, closeRef.current, 0);
          break;
        case 1:
          if (!isNullish(closeRef.current)) {
            onFocusChange(closeRef.current, textRef.current, 1);
          }
          break;
      }
    }
  }, [hasTokenCountChanged, tabIndex, onFocusChange]);

  const customTooltip = props.tooltip;
  const iconAriaLabel = iconA11yInfo?.ariaLabel ?? iconA11yInfo?.tooltip;
  const defaultAriaLabel = customTooltip
    ? [props.value, customTooltip].join(', ')
    : iconAriaLabel
    ? [props.value, iconAriaLabel].join(', ')
    : undefined;
  const defaultTooltip = iconA11yInfo?.tooltip;

  const showingIconOrTooltip = props.icon || customTooltip;
  const defaultCloseButtonTooltip = props.includeDropdown
    ? localize('groupToken.closeIcon.tooltip.label')
    : localize('token.closeIcon.tooltip.label');
  const closeButtonTooltip = props.closeButtonProps?.tooltip ?? defaultCloseButtonTooltip;
  const defaultCloseButtonAriaLabel = [closeButtonTooltip, props.value].join(', ');

  if (props.asButton) {
    return (
      <BaseButton
        text={props.value}
        icon={props.icon}
        variant={ButtonVariant.Small}
        className={cx(props.className, cn.tokenButton)}
        style={{ ...style, ...props.style }}
        onPress={props.onClick}
        tabIndex={props.tabIndex}
        testId={testId}
        ref={setTokenContainerRef}
        tooltip={props.tooltip ?? defaultTooltip}
        ariaLabel={props.ariaLabel ?? defaultAriaLabel}
        ariaDescribedBy={props.ariaDescribedBy}
        onKeyDown={props.onKeyDown}
        onFocus={props.onFocus}
        tokens={({ state }) => ({
          fg: 'var(--token-fg)',
          bg: 'var(--token-bg)',
          bd: state === 'hover' || state === 'active' ? 'var(--token-bd)' : 'transparent',
          filter: 'none',
        })}
      />
    );
  }

  return (
    <TokenDesignContainer
      role="row"
      spacing="xs"
      className={cx(
        props.className,
        cn.token,
        props.includeDropdown && cn.includeDropdown,
        props.includeDropdown && menuIsOpen && cn.dropdownOpen
      )}
      style={{ ...style, ...props.style }}
      onClick={props.onClick}
      data-testid={testId}
      ref={setTokenContainerRef}
      tabIndex={-1}
      aria-describedby={props.ariaDescribedBy}
      onKeyDown={(e) => {
        if (menuIsOpen) {
          return;
        }
        if (props.onKeyDown) {
          props.onKeyDown(e);
        } else {
          defaultOnKeyDown(e);
        }
      }}
      onFocus={props.onFocus}
      dndProps={
        props.allowDrag
          ? {
              index: props.index ?? -1,
              id: props.id ?? props.value,
              dndPrefix,
            }
          : undefined
      }
    >
      <div
        role="gridcell"
        className={cx(
          cn.textWrapper,
          props.includeDropdown && cn.groupTokenWrapper,
          !props.includeDropdown && !props.suppressFocusVisible && cn.focusVisible
        )}
        aria-label={props.ariaLabel ?? defaultAriaLabel}
        title={customTooltip ?? defaultTooltip}
        // this div is only focusable when there is an icon
        // or if it has a custom tooltip
        // because we want the icon and the text
        // and the custom tooltip to be read by
        // assessible technology, NOVACOMMONS-12697
        tabIndex={
          showingIconOrTooltip ? (tabIndex === 0 && focusRef.current === 0 ? 0 : -1) : undefined
        }
        ref={showingIconOrTooltip ? textRef : undefined}
      >
        {props.icon ? (
          <Icon
            {...props.icon}
            tokens={() => {
              return { fg: 'var(--token-fg)' };
            }}
            tooltip=""
            ariaHidden
            className={cx(cn.icon, props.icon.className)}
          />
        ) : null}
        {props.includeDropdown ? (
          <BaseMenuButton
            testId={postfixTestId(props.testId, 'menuButton')}
            ref={textRef as RefObject<HTMLButtonElement>}
            menuDockingElement={tokenContainerRef.current}
            variant={ButtonVariant.LightSmall}
            text={props.value}
            className={cx(cn.groupTokenMenu, !props.suppressFocusVisible && cn.focusVisible)}
            focusable={tabIndex === 0 && focusRef.current === 0 ? true : false}
            tokens={() => ({ filter: 'none' })}
            sections={menuSections}
            onItemSelected={props.onItemSelected ?? (() => {})}
            onItemClosed={([itemLocation]) => {
              const [, itemIndex] = itemLocation;
              props.onCloseItem?.(itemIndex);
            }}
            onOpen={() => setMenuIsOpen(true)}
            onDismiss={() => setMenuIsOpen(false)}
            onMouseDown={() => setMenuIsOpen(true)}
            onKeyDown={(e) => {
              if (['Enter', ' '].includes(e.key)) {
                setMenuIsOpen(true);
              }
            }}
          />
        ) : (
          <Text
            testId={postfixTestId(props.testId, 'text')}
            className={cx(cn.text, !props.icon && !props.suppressFocusVisible && cn.focusVisible)}
            tokens={() => {
              return { fg: 'var(--token-fg)' };
            }}
            wrapText={false}
            ref={showingIconOrTooltip ? undefined : textRef}
            tabIndex={
              showingIconOrTooltip ? undefined : tabIndex === 0 && focusRef.current === 0 ? 0 : -1
            }
          >
            {props.value}
          </Text>
        )}
      </div>
      {!props.readOnly ? (
        <div
          role="gridcell"
          className={cx(
            cn.closeIconContainer,
            props.includeDropdown && cn.groupTokenCloseIconContainer
          )}
          onKeyPress={(e) => {
            e.preventDefault();
          }}
        >
          <div
            data-testid={postfixTestId(props.testId, 'closeIconFocusContainer')}
            role="button"
            title={closeButtonTooltip}
            aria-label={props.closeButtonProps?.ariaLabel ?? defaultCloseButtonAriaLabel}
            aria-labelledby={props.closeButtonProps?.ariaLabelledBy}
            ref={closeRef}
            className={cx(
              cn.closeIconFocusContainer,
              !props.suppressFocusVisible && cn.focusVisible
            )}
            tabIndex={tabIndex === 0 && focusRef.current === 1 ? 0 : -1}
            onKeyUp={(e: ReactKeyboardEvent) => {
              if (['Enter', ' '].includes(e.key)) {
                // onkeyup prevents the group token's menuButton to open menu popper when close icon of the
                // token ahead is key-pressed and focus falls on the menuButton.
                props.onClose?.();
              }
            }}
            onClick={(e) => {
              props.onClose?.();
              e.stopPropagation();
            }}
          >
            <Icon
              testId={postfixTestId(props.testId, 'close')}
              className={cn.icon}
              tokens={() => {
                return { fg: menuIsOpen ? 'var(--token-expanded-fg)' : 'var(--token-fg)' };
              }}
              icon={CloseTab}
              tooltip=""
              ariaHidden
            />
          </div>
        </div>
      ) : null}
    </TokenDesignContainer>
  );
});

function getTokenThemeTokens(
  dt: DesignTokens,
  variant: TokenDesignProps['variant'],
  readOnly: TokenDesignProps['readOnly']
) {
  if (dt.enableNovaTokens) {
    switch (variant) {
      case 'primary':
        return {
          ...dt.statePrimaryNova.normal,
          bd: dt.themeVariant === 'high-contrast' ? dt.c.colorPrimary : 'transparent',
          'selected-bd':
            dt.themeVariant !== 'high-contrast' && readOnly ? 'transparent' : dt.c.colorPrimary,
          'selected-bd-width': dt.themeVariant === 'high-contrast' && !readOnly ? '2px' : '1px',
          fg: dt.c.colorTextDefault,
          bg: setAlpha(dt.c.colorPrimary, 0.16),
        };

      case 'secondary':
        return {
          ...dt.stateSecondaryNova.normal,
          bd: dt.themeVariant === 'high-contrast' ? dt.c.colorSecondary : 'transparent',
          'selected-bd':
            dt.themeVariant !== 'high-contrast' && readOnly ? 'transparent' : dt.c.colorSecondary,
          'selected-bd-width': dt.themeVariant === 'high-contrast' && !readOnly ? '2px' : '1px',
          fg: dt.c.colorTextDefault,
          bg: setAlpha(dt.c.colorSecondary, 0.16),
        };

      default:
        return {
          ...dt.stateStandardNova.normal,
          bd: dt.themeVariant === 'high-contrast' ? dt.c.colorNeutral : 'transparent',
          'selected-bd':
            dt.themeVariant !== 'high-contrast' && readOnly ? 'transparent' : dt.c.colorNeutral,
          'selected-bd-width': dt.themeVariant === 'high-contrast' && !readOnly ? '2px' : '1px',
          fg: dt.c.colorTextDefault,
          bg: setAlpha(dt.c.colorNeutral, 0.16),
        };
    }
  }

  // Unfortunately, Token uses its own set of design tokens.
  const darkOpacity = 0.4;
  const hcOpacity = 0.05;
  const lightOpacity = 0.1;

  switch (variant) {
    case TokenVariant.Primary:
      switch (dt.baseReportThemeName) {
        case 'highcontrast2018':
        case 'highcontrast2017':
          return dt.statePrimary.normal;
      }
      switch (dt.themeVariant) {
        case 'dark':
          return {
            fg: dt.c.white,
            bg: setAlpha(dt.c.p, darkOpacity),
            bd: dt.c.p,
          };
        case 'high-contrast':
          return {
            fg: dt.c.p3,
            bg: setAlpha(dt.c.p3, hcOpacity),
            bd: dt.c.p3,
          };
        default:
          return {
            fg: dt.c.p_1,
            bg: setAlpha(dt.c.p, lightOpacity),
            bd: dt.c.p,
          };
      }
    case TokenVariant.Secondary:
      switch (dt.baseReportThemeName) {
        case 'highcontrast2018':
        case 'highcontrast2017':
          return dt.stateSecondary.normal;
      }
      switch (dt.themeVariant) {
        case 'dark':
          return {
            fg: dt.c.white,
            bg: setAlpha(dt.c.e1, darkOpacity),
            bd: dt.c.e1,
          };
        case 'high-contrast':
          return {
            fg: dt.c.e2,
            bg: setAlpha(dt.c.e2, hcOpacity),
            bd: dt.c.e2,
          };
        default:
          return {
            fg: dt.c.e,
            bg: setAlpha(dt.c.e, lightOpacity),
            bd: dt.c.e,
          };
      }
    default:
      switch (dt.baseReportThemeName) {
        case 'highcontrast2018':
        case 'highcontrast2017':
          return dt.stateStandard.normal;
      }
      switch (dt.themeVariant) {
        case 'dark':
          return {
            fg: dt.c.white,
            bg: setAlpha(dt.c.n1, darkOpacity),
            bd: dt.c.n1,
          };
        case 'high-contrast':
          return {
            fg: dt.c.white,
            bg: setAlpha(dt.c.white, hcOpacity),
            bd: dt.c.white,
          };
        default:
          return {
            fg: dt.c.n_3,
            bg: setAlpha(dt.c.n1, lightOpacity),
            bd: dt.c.n1,
          };
      }
  }
}
